/**
 * Jurisdiction Color Mapping
 *
 * Each jurisdiction is assigned a unique primary color based on the country's flag.
 * These colors are used for visualization throughout the application.
 *
 * This mirrors the backend mapping in backend/src/modules/companies/constants/jurisdiction-colors.ts
 */

export const JURISDICTION_COLORS: Record<string, string> = {
  // African Countries - Strategically distinct colors across full spectrum
  'Tanzania': '#2E7D32',           // Forest Green (dark rich green)
  'Tanzania Asma': '#2E7D32',      // Forest Green (alternate name)
  'DRC': '#1976D2',                // Blue (primary blue)
  'Zambia': '#F57C00',             // Orange (vibrant orange)
  'Zambia M': '#F57C00',           // Orange (alternate name)
  'Zambia old': '#E65100',         // Dark Orange (darker variant)
  'Mozambique': '#5D4037',         // Brown (earth tone)
  'Malawi': '#9C27B0',             // Purple (true purple - distinct from all reds/pinks)
  'Uganda': '#FBC02D',             // Yellow (golden yellow)
  'Kenya': '#D32F2F',              // Red (pure red - distinct from purple/pink)
  'Zimbabwe': '#689F38',           // Light Green (yellowish green - distinct from forest green)
  'ZIMBABWE': '#689F38',           // Light Green (uppercase variant)
  'Angola': '#D32F2F',             // Red (same as Kenya - grouped)

  // European Countries
  'UK': '#303F9F',                 // Indigo (deep blue-purple)
  'UNITED KINGDOM': '#303F9F',     // Indigo (alternate name)
  'United Kingdom': '#303F9F',     // Indigo (alternate name)
  'UK old': '#283593',             // Darker Indigo (variant)
  'Isle of Man': '#C2185B',        // Pink (deep pink/magenta - distinct from red and purple)

  // Middle East
  'UAE': '#00796B',                // Teal (blue-green - distinct from greens and blues)
  'United Arab Emirates': '#00796B', // Teal (alternate name)

  // Americas
  'Canada': '#D32F2F',             // Red (grouped with Kenya/Angola)

  // Asia
  'India': '#FF6F00',              // Amber (golden orange - distinct from orange and yellow)
  'INDIA': '#FF6F00',              // Amber (uppercase variant)

  // Caribbean/British Overseas Territories
  'BVI': '#0097A7',                // Cyan (bright cyan - distinct from blue and teal)
  'B V I': '#0097A7',              // Cyan (spaced variant)
};

/**
 * Get color for a jurisdiction
 * @param jurisdiction - The jurisdiction name
 * @returns Hex color code or default gray if jurisdiction not found
 */
export function getJurisdictionColor(jurisdiction: string | null | undefined): string {
  if (!jurisdiction) return '#999999';
  return JURISDICTION_COLORS[jurisdiction] || '#999999';
}

/**
 * Darken a hex color by a given factor
 * @param hex - Hex color code (e.g., '#FF0000')
 * @param factor - Darkening factor (0-1, where 1 is black)
 * @returns Darkened hex color
 */
export function darkenColor(hex: string, factor: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by factor
  const darkenedR = Math.round(r * (1 - factor));
  const darkenedG = Math.round(g * (1 - factor));
  const darkenedB = Math.round(b * (1 - factor));

  // Convert back to hex
  return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
}

/**
 * Lighten a hex color by a given factor
 * @param hex - Hex color code (e.g., '#FF0000')
 * @param factor - Lightening factor (0-1, where 1 is white)
 * @returns Lightened hex color
 */
export function lightenColor(hex: string, factor: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Lighten by factor (move towards 255)
  const lightenedR = Math.round(r + (255 - r) * factor);
  const lightenedG = Math.round(g + (255 - g) * factor);
  const lightenedB = Math.round(b + (255 - b) * factor);

  // Convert back to hex
  return `#${lightenedR.toString(16).padStart(2, '0')}${lightenedG.toString(16).padStart(2, '0')}${lightenedB.toString(16).padStart(2, '0')}`;
}

/**
 * Get all jurisdictions with their colors
 * @returns Array of unique jurisdiction-color pairs
 */
export function getAllJurisdictionColors(): Array<{ jurisdiction: string; color: string }> {
  const seen = new Set<string>();
  const unique: Array<{ jurisdiction: string; color: string }> = [];

  Object.entries(JURISDICTION_COLORS).forEach(([jurisdiction, color]) => {
    const key = `${jurisdiction}:${color}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push({ jurisdiction, color });
    }
  });

  return unique;
}
